import React, { useEffect, useState } from 'react'
import { components, constants, useTranslation } from 'cng-web-lib'
import TradePartyFormProperties from './TradePartyFormProperties'
import LocalTable from '../../../components/aciacehighway/LocalTable'
import Alert from '../../../components/Alert'
import DialogForm from '../../../components/aciacehighway/DialogForm'
import { Grid, Card, Typography, Box } from '@material-ui/core'
import Namespace from '../../../constants/locale/Namespace'
import NbciUsInvoiceKeys from '../../../constants/locale/key/NbciUsInvoice'
import { useFormContext } from 'react-hook-form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const {
  table: { useFetchCodeMaintenanceLookup }
} = components

const {
  filter: { EQUAL },
  CodeMaintenanceType
} = constants

function TradePartySection(props) {
  const { tradeParty, setTradePartyDirty } = props
  const [lookups, setLookups] = useState(null)
  const [dialog, setDialog] = useState({ open: false, tradeParty: null })
  const fetchCodeMaintenanceLookup = useFetchCodeMaintenanceLookup()
  const { translate } = useTranslation(Namespace.NBCI_US_INVOICE)
  const translatedTextsObject = makeTranslatedTextsObject()
  const { getValues, setValue } = useFormContext()

  function makeTranslatedTextsObject() {
    const tradeParty = translate(
      Namespace.NBCI_US_INVOICE,
      NbciUsInvoiceKeys.TradeParty.TITLE
    )
    const partyIdn = translate(
      Namespace.NBCI_US_INVOICE,
      NbciUsInvoiceKeys.TradeParty.PARTY_IDENTIFIER_NUMBER
    )
    const partyName = translate(
      Namespace.NBCI_US_INVOICE,
      NbciUsInvoiceKeys.TradeParty.PARTY_NAME
    )
    const partyType = translate(
      Namespace.NBCI_US_INVOICE,
      NbciUsInvoiceKeys.TradeParty.PARTY_TYPE
    )
    const address = translate(
      Namespace.NBCI_US_INVOICE,
      NbciUsInvoiceKeys.TradeParty.PARTY_ADDRESS
    )
    const country = translate(
      Namespace.NBCI_US_INVOICE,
      NbciUsInvoiceKeys.TradeParty.PARTY_COUNTRY
    )

    return {
      partyIdn,
      partyType,
      partyName,
      address,
      country,
      tradeParty
    }
  }

  useEffect(() => {
    Promise.all([
      fetchCodeMaintenanceLookup(CodeMaintenanceType.CODE_MASTER, undefined, [
        { field: 'codeType', operator: EQUAL, value: 'PARTY_TYPE_US_MASTER' }
      ]),
      fetchCodeMaintenanceLookup(CodeMaintenanceType.COUNTRY)
    ]).then(([partyType, country]) => {
      setLookups({ partyType, country })
    })
  }, [])

  function getLookupValue(name, value) {
    if (!lookups) return value

    return lookups[name] && lookups[name][value] ? lookups[name][value] : value
  }

  const columns = [
    {
      title: translatedTextsObject.partyIdn,
      field: 'partyIdentifierNumber',
      filterConfig: { type: 'textfield' }
    },
    {
      title: translatedTextsObject.partyType,
      field: 'partyType',
      filterConfig: {
        type: 'select',
        options: lookups?.partyType
          ? Object.entries(lookups.partyType).map(([code, label]) => ({
              label: label,
              value: code
            }))
          : []
      },
      render: (data) => getLookupValue('partyType', data.partyType)
    },
    {
      title: translatedTextsObject.partyName,
      field: 'partyName',
      filterConfig: { type: 'textfield' }
    },
    {
      title: translatedTextsObject.address,
      field: 'partyAddress',
      filterConfig: { type: 'textfield' }
    },
    {
      title: translatedTextsObject.country,
      field: 'partyCountry',
      filterConfig: { type: 'textfield' },
      render: (data) => getLookupValue('country', data.countryCode)
    }
  ]

  function handleAddTradeParty(data) {
    const tradeParty = [...getValues('tradeParty'), data].map(
      (party, index) => ({ ...party, _id: index + 1 })
    )

    setValue('tradeParty', tradeParty)
    setDialog({ open: false, tradeParty: null })
    setTradePartyDirty(true)
  }

  function handleDeleteTradeParty(data) {
    const tradeParty = [...getValues('tradeParty')].filter((party) =>
      party.id ? party.id !== data.id : party._id !== data._id
    )

    setValue('tradeParty', tradeParty)
    setTradePartyDirty(true)
  }

  function handleEditTradeParty(data) {
    const tradeParty = [...getValues('tradeParty')]
    const index = tradeParty.findIndex((party) =>
      party.id ? party.id === data.id : party._id === data._id
    )

    tradeParty[index] = data

    setValue('tradeParty', tradeParty)
    setDialog({ open: false, tradeParty: null })
    setTradePartyDirty(true)
  }

  return (
    <Card variant='outlined'>
      <LocalTable
        actions={[
          {
            buttonProps: { size: 'medium', color: 'secondary' },
            icon: ['fal', 'plus-circle'],
            label: 'Add party',
            onClick: () => setDialog({ open: true, tradeParty: null })
          }
        ]}
        columns={columns}
        data={tradeParty}
        header={
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography style={{ fontWeight: 600 }}>
                {translatedTextsObject.tradeParty}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box display='flex' justifyContent='flex-end'>
                <Alert
                  severity='info'
                  icon={
                    <FontAwesomeIcon icon={['fal', 'info-circle']} size='xs' />
                  }
                >
                  <Typography style={{ lineHeight: 1.2 }} variant='caption'>
                    <Typography style={{ fontWeight: 600 }} variant='inherit'>
                      NOTE:&nbsp;
                    </Typography>
                    Sold To, Customer and Exporter parties are mandatory.
                  </Typography>
                </Alert>
              </Box>
            </Grid>
          </Grid>
        }
        rowActions={[
          {
            icon: ['fal', 'pen'],
            onClick: (rowData) =>
              setDialog({ open: true, tradeParty: rowData }),
            tooltip: () => 'Edit'
          },
          {
            icon: ['fal', 'trash'],
            onClick: handleDeleteTradeParty,
            tooltip: () => 'Delete'
          }
        ]}
      />
      <DialogForm
        formProperties={TradePartyFormProperties}
        fullWidth
        maxWidth='md'
        open={dialog.open}
        onClose={() => setDialog({ open: false, tradeParty: null })}
        onSubmit={
          dialog.tradeParty ? handleEditTradeParty : handleAddTradeParty
        }
        title={`${dialog.tradeParty ? 'Edit' : 'Add'} trade party`}
        value={dialog.tradeParty}
      />
    </Card>
  )
}

export default TradePartySection
